/**
 * One column header of the Home doc-list, rendered per visible column by
 * `DocListTableHeader`: the column menu (filter and sort), the sort toggle, the
 * drag grip that reorders columns and the resize handle that sizes them.
 *
 * In card mode the cell is a chip in a wrapping strip. There are no tracks to
 * size, so the resize handle is not rendered; the grip stays visible but disabled,
 * and the menu gains "Column options", the touch path to the context menu that a
 * right-click opens on desktop.
 */
import { useCallback, useContext, useRef, type CSSProperties } from 'react';
import { Typography, theme, Button, Tooltip } from 'antd';
import {
  CaretDownOutlined,
  CaretUpOutlined,
  CheckOutlined,
  HolderOutlined,
  FilterFilled,
} from '@ant-design/icons';
import { CSS } from '@dnd-kit/utilities';
import { useSortable } from '@dnd-kit/sortable';
import { useSnapshot } from 'valtio';
import type { MenuProps } from 'antd';

import type { ColumnSpec, BaseListDocument } from '../../DocList.types';
import { DocListStoreContext } from '../../createDocListTableStore';
import { MessageContext } from '@/providers/MessageContext';
import { ACTIONS_COLUMN_ID } from '../constants';
import DocListColumnMenu from './DocListColumnMenu';

const { Text } = Typography;

type ResizeHandleStyle = CSSProperties & {
  '--column-resize-color': string;
  '--column-resize-color-active': string;
};

interface DraggableHeaderCellProps<TDoc extends BaseListDocument> {
  column: ColumnSpec<TDoc>;
  facetedUniqueValues: Map<string, number>;
  getLabel?: (columnId: string, value: unknown) => string;
  frozenLeftOffset?: number;
  onContextMenu?: (columnId: string, position: { x: number; y: number }) => void;
  cardMode?: boolean;
}

function DraggableHeaderCell<TDoc extends BaseListDocument>({
  column,
  facetedUniqueValues,
  getLabel,
  frozenLeftOffset,
  onContextMenu,
  cardMode = false,
}: DraggableHeaderCellProps<TDoc>) {
  const { token } = theme.useToken();
  const store = useContext(DocListStoreContext);
  const { messageApi } = useContext(MessageContext);
  const snap = useSnapshot(store);

  const columnId = column.id;
  const isActionsColumn = columnId === ACTIONS_COLUMN_ID;
  const canDrag = !isActionsColumn;
  const hasSortCapability = !isActionsColumn && Boolean(column.serverSortField);
  const canSort = hasSortCapability && !snap.sortDisabledReason;
  const canResize = !isActionsColumn && !cardMode;
  const isFrozen = frozenLeftOffset !== undefined;
  const filterDisabled = false;
  const hasFilterCapability = (column.filterable ?? false) && Boolean(column.serverFilterField);
  const size = snap.columnSizing[columnId] ?? column.size ?? 150;
  const minSize = column.minSize ?? Math.round((column.size ?? 150) / 2);
  const maxSize = column.maxSize ?? (column.size ?? 150) * 4;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: columnId, disabled: cardMode });

  const chipRef = useRef<HTMLDivElement | null>(null);
  const setCellRef = useCallback((node: HTMLDivElement | null) => {
    chipRef.current = node;
    setNodeRef(node);
  }, [setNodeRef]);

  const openColumnOptions = () => {
    const rect = chipRef.current?.getBoundingClientRect();
    if (rect && onContextMenu) {
      onContextMenu(columnId, { x: rect.left, y: rect.bottom });
    }
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const sortEntry = snap.sorting.find(s => s.id === columnId);
  const sortDirection: 'asc' | 'desc' | false = sortEntry
    ? (sortEntry.desc ? 'desc' : 'asc')
    : false;

  const filterEntry = snap.columnFilters.find(f => f.id === columnId);
  const filterValue = filterEntry?.value;
  const hasActiveFilter = hasFilterCapability && filterValue !== undefined;

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isActionsColumn && onContextMenu) {
      onContextMenu(columnId, { x: e.clientX, y: e.clientY });
    }
  };

  /* eslint-disable react-hooks/immutability */
  const toggleSort = () => {
    if (!canSort) return;
    if (sortDirection === false) {
      store.sorting = [{ id: columnId, desc: true }];
    } else if (sortDirection === 'desc') {
      store.sorting = [{ id: columnId, desc: false }];
    } else {
      store.sorting = store.sorting.filter(s => s.id !== columnId);
    }
  };

  const setSortDesc = () => {
    if (!canSort) return;
    store.sorting = [{ id: columnId, desc: true }];
  };

  const setSortAsc = () => {
    if (!canSort) return;
    store.sorting = [{ id: columnId, desc: false }];
  };

  const clearSort = () => {
    if (!canSort) return;
    store.sorting = store.sorting.filter(s => s.id !== columnId);
  };

  const handleFilterChange = (value: unknown) => {
    if (value !== undefined && value !== null) {
      const activeIds = store.columnFilters.map(f => f.id);
      const idsToRemove = store.resolveFilterConflicts(activeIds, columnId);
      if (idsToRemove.length > 0) {
        const removedLabels = idsToRemove.join(', ');
        messageApi.info(`Cleared incompatible filter${idsToRemove.length > 1 ? 's' : ''}: ${removedLabels}`);
      }
      for (const removeId of idsToRemove) {
        const idx = store.columnFilters.findIndex(f => f.id === removeId);
        if (idx !== -1) store.columnFilters.splice(idx, 1);
      }
    }

    const existing = store.columnFilters.findIndex(f => f.id === columnId);
    if (value === undefined || value === null) {
      if (existing !== -1) {
        store.columnFilters.splice(existing, 1);
      }
    } else if (existing !== -1) {
      store.columnFilters[existing] = { id: columnId, value };
    } else {
      store.columnFilters.push({ id: columnId, value });
    }
  };

  const handleFilterClear = () => {
    const existing = store.columnFilters.findIndex(f => f.id === columnId);
    if (existing !== -1) {
      store.columnFilters.splice(existing, 1);
    }
  };
  /* eslint-enable react-hooks/immutability */

  const handleResizeStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    const startX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const startWidth = size;

    const onMouseMove = (moveEvent: MouseEvent | TouchEvent) => {
      const clientX = 'touches' in moveEvent
        ? moveEvent.touches[0].clientX
        : (moveEvent as MouseEvent).clientX;
      const delta = clientX - startX;
      const newWidth = Math.min(maxSize, Math.max(minSize, startWidth + delta));
      store.columnSizing[columnId] = newWidth;
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('touchmove', onMouseMove);
      document.removeEventListener('touchend', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('touchmove', onMouseMove);
    document.addEventListener('touchend', onMouseUp);
  };

  const resizeHandleStyle: ResizeHandleStyle = {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    width: 4,
    boxSizing: 'content-box',
    zIndex: 30,
    userSelect: 'none',
    touchAction: 'none',
    '--column-resize-color': token.colorTextTertiary,
    '--column-resize-color-active': token.colorPrimary,
  };

  const sortButton = (
    <Button
      aria-label={`doclist-sort-toggle-${columnId}`}
      type="text"
      size="small"
      disabled={!canSort}
      icon={
        sortDirection === 'desc'
          ? <CaretDownOutlined />
          : sortDirection === 'asc'
            ? <CaretUpOutlined />
            : <CaretDownOutlined style={{ opacity: 0.4 }} />
      }
      onClick={(e) => {
        e.stopPropagation();
        toggleSort();
      }}
      style={{
        padding: 0,
        pointerEvents: canSort ? undefined : 'none',
        color:
          !canSort
            ? token.colorTextDisabled
            : sortDirection === false
              ? token.colorTextSecondary
              : token.colorPrimary,
      }}
    />
  );

  const sortActionItems: MenuProps['items'] = hasSortCapability
    ? [
      {
        key: 'sort-desc',
        icon: sortDirection === 'desc' ? <CheckOutlined /> : undefined,
        label: 'Sort descending',
        disabled: !canSort,
        onClick: setSortDesc,
      },
      {
        key: 'sort-asc',
        icon: sortDirection === 'asc' ? <CheckOutlined /> : undefined,
        label: 'Sort ascending',
        disabled: !canSort,
        onClick: setSortAsc,
      },
      {
        key: 'sort-clear',
        label: 'Clear sort',
        disabled: !canSort || sortDirection === false,
        onClick: clearSort,
      },
    ]
    : [];

  const columnActionItems: MenuProps['items'] = cardMode
    ? [
      ...sortActionItems,
      ...(sortActionItems.length > 0 ? [{ type: 'divider' as const, key: 'column-options-divider' }] : []),
      { key: 'column-options', label: 'Column options', onClick: openColumnOptions },
    ]
    : sortActionItems;

  const holder = (
    <HolderOutlined
      {...(cardMode
        ? { 'aria-disabled': true, onClick: (e: React.MouseEvent) => e.stopPropagation() }
        : listeners)}
      style={{
        cursor: cardMode ? 'not-allowed' : 'grab',
        fontSize: token.fontSizeSM,
        color: cardMode ? token.colorTextDisabled : token.colorTextTertiary,
        touchAction: cardMode ? undefined : 'none',
      }}
      aria-label={`Drag to reorder ${columnId} column`}
    />
  );
  const holderIcon = cardMode
    ? <Tooltip title="Reorder columns on a wider screen">{holder}</Tooltip>
    : holder;

  const cellStyle: CSSProperties = {
    ...style,
    padding: `${token.paddingXS}px ${token.paddingSM}px`,
    backgroundColor: token.colorBgElevated,
    minWidth: 0,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderStyle: 'solid',
    borderColor: token.colorBorderSecondary,
    ...(cardMode
      ? {
        borderWidth: 1,
        borderRadius: token.borderRadiusSM,
        position: 'relative',
      }
      : {
        borderWidth: '0 0 1px 0',
        zIndex: isDragging ? 20 : isFrozen || isActionsColumn ? 15 : 10,
        position: isActionsColumn || isFrozen ? 'sticky' : 'relative',
        left: isFrozen ? `${frozenLeftOffset}px` : undefined,
        right: isActionsColumn ? 0 : undefined,
        boxShadow: isFrozen ? `inset -1px 0 0 ${token.colorBorder}` : undefined,
      }),
  };

  return (
    <div
      ref={setCellRef}
      style={cellStyle}
      {...(canDrag && !cardMode ? attributes : {})}
      aria-label={`doclist-table-header-cell-${columnId}`}
      role="columnheader"
      onContextMenu={handleContextMenu}
    >
      {isActionsColumn ? null : (
        <DocListColumnMenu
          columnId={columnId}
          label={column.header}
          filterable={hasFilterCapability}
          filterDisabled={filterDisabled}
          filterValue={filterValue}
          onFilterChange={handleFilterChange}
          onFilterClear={handleFilterClear}
          facetedUniqueValues={facetedUniqueValues}
          columnWidth={size}
          filterDropdownWidth={column.filterDropdownWidth}
          getLabel={getLabel}
          actionItems={columnActionItems}
          disabled={false}
          ariaLabel={`doclist-table-header-actions-${columnId}`}
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            gap: token.marginXS,
            cursor: 'pointer',
            userSelect: 'none',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: token.marginXXS,
          }}>
            {canDrag && holderIcon}
            <Text ellipsis>
              {column.header}
            </Text>
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: token.marginXXS
          }}>
            {hasActiveFilter && (
              <FilterFilled
                aria-label={`doclist-filter-active-${columnId}`}
                style={{
                  color: token.colorPrimary,
                  fontSize: token.fontSizeSM,
                  flexShrink: 0,
                }}
              />
            )}
            {hasSortCapability && (
              snap.sortDisabledReason
                ? (
                  <Tooltip title={snap.sortDisabledReason}>
                    <span
                      style={{ display: 'inline-flex', cursor: 'not-allowed' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {sortButton}
                    </span>
                  </Tooltip>
                )
                : sortButton
            )}
          </div>
        </DocListColumnMenu>
      )}
      {canResize && (
        <div
          className="column-resize-handle"
          aria-label={`resize-handle-${columnId}`}
          style={resizeHandleStyle}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={handleResizeStart}
          onTouchStart={handleResizeStart}
        />
      )}
    </div>
  );
}

export default DraggableHeaderCell;
